import openai from './openaiClient';
import {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'مفتاح API غير صالح. يرجى التحقق من إعدادات OpenAI.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'تم تجاوز الحصة أو فشل التفويض. قد تكون قد تجاوزت حدود الاستخدام.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'تم تجاوز حد المعدل. يرجى الانتظار لحظة والمحاولة مرة أخرى.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'خدمة OpenAI غير متاحة حالياً. يرجى المحاولة لاحقاً.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'غير قادر على الاتصال بخدمة OpenAI. يرجى التحقق من مفتاح API واتصال الإنترنت.' };
  } else {
    return { isInternal: false, message: error?.message || 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.' };
  }
}

/**
 * Generates intelligent Arabic property recommendations using GPT-5
 * @param {Object} client - Client object with preferences and requirements
 * @param {Array} properties - Available properties to match against
 * @returns {Promise<Object>} Recommendations with scores and reasoning
 */
export async function generateArabicPropertyRecommendations(client, properties) {
  try {
    // Prepare client profile
    const clientProfile = `
معلومات العميل:
- الاسم: ${client?.name || 'غير محدد'}
- النوع: ${client?.type || 'غير محدد'}
- الميزانية: ${client?.budget ? `${client?.budget} ريال` : 'غير محددة'}
- نطاق السعر: ${client?.minPrice || 0} - ${client?.maxPrice || 'غير محدد'} ريال
- المدينة المفضلة: ${client?.preferredCity || 'غير محددة'}
- الأحياء المفضلة: ${client?.preferredNeighborhoods?.join('، ') || 'غير محددة'}
- نوع العقار المطلوب: ${client?.propertyType || 'غير محدد'}
- الحد الأدنى للمساحة: ${client?.minArea || 'غير محدد'} متر مربع
- عدد الغرف المطلوب: ${client?.bedrooms || 'غير محدد'}
- عدد الحمامات: ${client?.bathrooms || 'غير محدد'}
- المميزات المطلوبة: ${client?.features?.join('، ') || 'غير محددة'}
- الملاحظات: ${client?.notes || 'لا توجد'}
`;

    // Prepare properties list
    const propertiesText = properties?.map((prop, index) => `
عقار ${index + 1}:
- المعرف: ${prop?.id}
- العنوان: ${prop?.title || 'غير محدد'}
- النوع: ${prop?.type || 'غير محدد'}
- السعر: ${prop?.price || 0} ريال
- المساحة: ${prop?.area || 0} متر مربع
- الموقع: ${prop?.city || ''} - ${prop?.neighborhood || ''}
- الغرف: ${prop?.bedrooms || 0}
- الحمامات: ${prop?.bathrooms || 0}
- المميزات: ${prop?.features?.join('، ') || 'لا توجد'}
- الوصف: ${prop?.description || 'لا يوجد'}
- الحالة: ${prop?.status || 'غير محدد'}
`)?.join('\n---\n');

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        {
          role: 'system',
          content: `أنت مستشار عقاري خبير متخصص في مطابقة العملاء مع العقارات المناسبة في السوق السعودي. مهمتك هي:
1. تحليل متطلبات العميل وتفضيلاته بعناية
2. تقييم كل عقار بناءً على مدى توافقه مع احتياجات العميل
3. تقديم توصيات مرتبة حسب الأولوية مع درجة التطابق (0-100)
4. شرح أسباب التوصية بكل عقار بالتفصيل
5. تحديد نقاط القوة والضعف لكل عقار
6. تقديم نصائح إضافية للعميل

يجب أن تكون توصياتك:
- دقيقة ومبنية على البيانات
- مراعية للميزانية والموقع
- شاملة لجميع المتطلبات
- واضحة ومفصلة باللغة العربية`
        },
        {
          role: 'user',
          content: `${clientProfile}\n\nالعقارات المتاحة:\n${propertiesText}\n\nيرجى تحليل هذه العقارات وتقديم أفضل 5 توصيات للعميل. لكل توصية، قدم:
1. معرف العقار
2. درجة التطابق (0-100)
3. سبب التوصية (3-4 جمل)
4. نقاط القوة (3-5 نقاط)
5. نقاط الضعف أو الاعتبارات (2-3 نقاط)
6. نصيحة خاصة للعميل

قدم الإجابة بتنسيق واضح ومنظم.`
        }
      ],
      reasoning_effort: 'high',
      verbosity: 'high',
      max_completion_tokens: 4000
    });

    const recommendationsText = response?.choices?.[0]?.message?.content;
    
    // Parse recommendations into structured format
    const recommendations = parseRecommendations(recommendationsText, properties);
    
    return {
      success: true,
      clientId: client?.id,
      totalProperties: properties?.length,
      recommendations,
      rawAnalysis: recommendationsText,
      generatedAt: new Date()?.toISOString()
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('خطأ في توليد التوصيات العقارية:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates personalized property description in Arabic
 * @param {Object} property - Property object
 * @param {Object} client - Client object for personalization
 * @returns {Promise<string>} Personalized Arabic description
 */
export async function generatePersonalizedDescription(property, client) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        {
          role: 'system',
          content: 'أنت كاتب محتوى عقاري محترف. اكتب وصفاً جذاباً ومخصصاً للعقار باللغة العربية يركز على ما يهم العميل.'
        },
        {
          role: 'user',
          content: `العقار: ${property?.title}\nالنوع: ${property?.type}\nالسعر: ${property?.price}\nالموقع: ${property?.city}\n\nالعميل يبحث عن: ${client?.propertyType}\nالميزانية: ${client?.budget}\n\nاكتب وصفاً مخصصاً (100-150 كلمة) يبرز مزايا العقار التي تناسب احتياجات العميل.`
        }
      ],
      reasoning_effort: 'minimal',
      verbosity: 'medium',
      max_completion_tokens: 500
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('خطأ في توليد الوصف المخصص:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Analyzes client preferences and suggests property search improvements
 * @param {Object} client - Client object
 * @returns {Promise<Object>} Search optimization suggestions
 */
export async function analyzeClientPreferences(client) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        {
          role: 'system',
          content: 'أنت مستشار عقاري خبير. قم بتحليل تفضيلات العميل واقترح تحسينات لبحثه عن العقار المناسب.'
        },
        {
          role: 'user',
          content: `تفضيلات العميل:\nالنوع: ${client?.type}\nالميزانية: ${client?.budget}\nالموقع: ${client?.preferredCity}\nنوع العقار: ${client?.propertyType}\n\nقدم 3-5 اقتراحات لتحسين البحث وزيادة فرص العثور على العقار المناسب.`
        }
      ],
      reasoning_effort: 'low',
      verbosity: 'medium',
      max_completion_tokens: 800
    });

    const suggestionsText = response?.choices?.[0]?.message?.content;
    const suggestions = suggestionsText?.split('\n')?.filter(line => line?.trim()?.match(/^[\d-•]/))?.map(s => s?.replace(/^[\d-•]\s*/, '')?.trim());

    return {
      success: true,
      clientId: client?.id,
      suggestions,
      rawAnalysis: suggestionsText
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('خطأ في تحليل تفضيلات العميل:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Parses AI-generated recommendations into structured format
 */
function parseRecommendations(text, properties) {
  const recommendations = [];
  const sections = text?.split(/عقار \d+:|توصية \d+:/i)?.filter(Boolean);

  sections?.forEach(section => {
    const rec = {
      propertyId: null,
      matchScore: 0,
      reasoning: '',
      strengths: [],
      weaknesses: [],
      advice: ''
    };

    // Extract property ID
    const idMatch = section?.match(/معرف[:\s]*(\w+)/i) || section?.match(/ID[:\s]*(\w+)/i);
    if (idMatch) {
      rec.propertyId = idMatch?.[1];
    }

    // Extract match score
    const scoreMatch = section?.match(/درجة التطابق[:\s]*(\d+)/i) || section?.match(/التطابق[:\s]*(\d+)/i);
    if (scoreMatch) {
      rec.matchScore = parseInt(scoreMatch?.[1]);
    }

    // Extract reasoning
    const reasoningMatch = section?.match(/سبب التوصية[:\s]*([\s\S]*?)(?=نقاط القوة|نقاط الضعف|النصيحة|$)/i);
    if (reasoningMatch) {
      rec.reasoning = reasoningMatch?.[1]?.trim();
    }

    // Extract strengths
    const strengthsMatch = section?.match(/نقاط القوة[:\s]*([\s\S]*?)(?=نقاط الضعف|الاعتبارات|النصيحة|$)/i);
    if (strengthsMatch) {
      const items = strengthsMatch?.[1]?.match(/[-•]\s*(.+?)(?=\n|$)/g);
      if (items) {
        rec.strengths = items?.map(item => item?.replace(/^[-•]\s*/, '')?.trim())?.filter(Boolean);
      }
    }

    // Extract weaknesses
    const weaknessesMatch = section?.match(/نقاط الضعف|الاعتبارات[:\s]*([\s\S]*?)(?=النصيحة|نقاط القوة|$)/i);
    if (weaknessesMatch) {
      const items = weaknessesMatch?.[1]?.match(/[-•]\s*(.+?)(?=\n|$)/g);
      if (items) {
        rec.weaknesses = items?.map(item => item?.replace(/^[-•]\s*/, '')?.trim())?.filter(Boolean);
      }
    }

    // Extract advice
    const adviceMatch = section?.match(/النصيحة|نصيحة[:\s]*([\s\S]*?)(?=عقار|توصية|$)/i);
    if (adviceMatch) {
      rec.advice = adviceMatch?.[1]?.trim();
    }

    // Find matching property
    if (rec?.propertyId) {
      const property = properties?.find(p => p?.id === rec?.propertyId);
      if (property) {
        rec.property = property;
        recommendations?.push(rec);
      }
    }
  });

  // Sort by match score
  return recommendations?.sort((a, b) => b?.matchScore - a?.matchScore);
}

export default {
  generateArabicPropertyRecommendations,
  generatePersonalizedDescription,
  analyzeClientPreferences
};